// bootstrap.jsx — esbuild entry point.
// Mounts the Preact mobile app ONLY when the viewport is narrow enough.
// Desktop (≥501px) passes through untouched; the existing static site renders.
//
// A landscape-orientation interstitial covers the screen on mobile-width
// devices held sideways; portrait is the design language (per the plan).

import { render } from 'preact';
import { App } from './app.jsx';
import { initDataBridge } from './data-bridge.js';

const MOBILE_QUERY = '(max-width: 500px)';
const LANDSCAPE_QUERY = '(orientation: landscape) and (max-width: 900px) and (max-height: 500px)';
const OPT_OUT_KEY = 'weatherfcc:v32826:mobile-opt-out';

// Phase 2: mobile redesign is the default on ≤500px viewports. Two escape hatches:
//   `?preview=off`     — disable for this visit AND persist opt-out (use the desktop site forever)
//   `?preview=mobile`  — clear the opt-out (back to redesign)
// Anyone explicitly opting out via `?preview=off` stays on the responsive desktop site.
function isOptedOut() {
  try {
    const sp = new URLSearchParams(globalThis.location?.search || '');
    const v = sp.get('preview');
    if (v === 'off')    { localStorage.setItem(OPT_OUT_KEY, '1'); return true; }
    if (v === 'mobile') { localStorage.removeItem(OPT_OUT_KEY);   return false; }
    return localStorage.getItem(OPT_OUT_KEY) === '1';
  } catch (_) { return false; }
}

function mountIfMobile() {
  if (!globalThis.matchMedia) return;
  if (!matchMedia(MOBILE_QUERY).matches) return;
  if (isOptedOut()) return;

  // Avoid double-mounting if bootstrap fires twice (e.g. PWA navigation).
  if (document.getElementById('m-mobile-root')) return;

  // Start the data fetches only now that we know this engine owns the screen.
  // The data bridge must NOT fetch at module scope — the bundle runs on every
  // viewport and would double the desktop engine's API burst (see data-bridge.js).
  initDataBridge();

  const root = document.createElement('div');
  root.id = 'm-mobile-root';
  document.body.appendChild(root);
  render(<App/>, root);

  // The desktop engine remains in the DOM behind the mobile screen. Keep its
  // invisible controls out of keyboard navigation and the accessibility tree.
  // Leave native dialogs eligible for showModal(), which escapes ancestor inert.
  let moveFocus = false;
  for (const sibling of document.body.children) {
    if (sibling === root || /^(SCRIPT|STYLE|LINK|DIALOG)$/.test(sibling.tagName)) continue;
    // The shared issue reporter stays visible above both desktop and mobile.
    if (sibling.id.startsWith('fcc-twg-')) continue;
    moveFocus ||= sibling.contains(document.activeElement);
    sibling.inert = true;
  }
  if (moveFocus) root.querySelector('main')?.focus({ preventScroll: true });

  // Lock body + html scroll forcefully. CSS `body:has(#m-mobile-root)` covers
  // modern browsers, but adding the class + the explicit styles here means
  // even older browsers (or any with weird :has bugs) get a fully-locked
  // outer scroll surface — so the only scroller is the PullToRefresh inner
  // div, and the desktop chrome underneath can never steal a touch.
  document.documentElement.classList.add('m-mobile-active');
  document.body.classList.add('m-mobile-active');

  // Lock down pinch-zoom and double-tap-zoom while the redesign is mounted —
  // app-like behavior is the design intent. Desktop visitors never reach this
  // branch (viewport gate), so their zoom is unaffected.
  const meta = document.querySelector('meta[name="viewport"]');
  if (meta) meta.setAttribute('content',
    'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover');

  // Best-effort PWA orientation lock — silently ignored outside standalone PWAs.
  try { screen.orientation?.lock?.('portrait').catch(() => {}); } catch (_) {}
}

function ensureLandscapeInterstitial() {
  const id = 'm-rotate-prompt';
  if (matchMedia(LANDSCAPE_QUERY).matches) {
    if (document.getElementById(id)) return;
    const el = document.createElement('div');
    el.id = id;
    el.setAttribute('role', 'alert');
    el.style.cssText = [
      'position:fixed', 'inset:0', 'z-index:10000',
      'display:flex', 'align-items:center', 'justify-content:center',
      'background:#0c0d0f', 'color:#f5f6f7',
      'padding:24px', 'text-align:center',
      'font-family:Inter,-apple-system,system-ui,sans-serif',
      'font-size:16px', 'line-height:1.5',
    ].join(';');
    // Style the children via CSSOM like the container above — inline
    // style="" attributes in innerHTML are dead under the tenant CSP
    // (style-src 'self'), and mobile.css can't cover this overlay (its
    // media gate is ≤500px; LANDSCAPE_QUERY allows up to 900px).
    el.innerHTML = '<div><div data-rotate-icon>↻</div>Please rotate to portrait.<br><span data-rotate-note>Landscape view is coming in a future release.</span></div>';
    el.querySelector('[data-rotate-icon]').style.cssText = 'font-size:38px;margin-bottom:14px';
    el.querySelector('[data-rotate-note]').style.cssText = 'opacity:.65;font-size:13px';
    document.body.appendChild(el);
  } else {
    const el = document.getElementById(id);
    if (el) el.remove();
  }
}

if (typeof globalThis !== 'undefined' && globalThis.document) {
  const start = () => { mountIfMobile(); ensureLandscapeInterstitial(); };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
  // Re-check on viewport changes (e.g. devtools resize, OS rotation).
  globalThis.addEventListener?.('resize', ensureLandscapeInterstitial);
  matchMedia(MOBILE_QUERY).addEventListener?.('change', mountIfMobile);
}
